import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@parcel/ui';
import { AppLogo, type AppLogoName } from '@parcel/ui/app-logo';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { ExternalLink, PlugZap, Trash2 } from 'lucide-react';
import * as React from 'react';
import {
  type DeveloperMcpGrant,
  type DeveloperMcpWorkspaceGrant,
  listDeveloperMcpGrants,
  revokeAllMyDeveloperMcpGrants,
  revokeDeveloperMcpGrant,
} from '../../lib/developer-mcp.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { IntegrationCard, IntegrationTile } from '../integration-card.js';
import { McpClientHero } from './mcp-client-hero.js';
import { McpManualConfig } from './mcp-manual-config.js';
import { SettingsPageHeader, SettingsShell } from './settings-shell.js';
import { ListSkeleton } from './settings-skeletons.js';

export const PARCEL_MCP_SERVER_URL = 'https://mcp.parcelengineering.com/mcp';

export const PARCEL_MCP_CLIENT_CONFIG_JSON = JSON.stringify(
  { mcpServers: { parcel: { url: PARCEL_MCP_SERVER_URL } } },
  null,
  2,
);

const SUPPORTED_SCOPES = [
  'data:read',
  'workspace:read',
  'workspace:write',
  'skills:read',
  'skills:write',
  'knowledge:read',
  'knowledge:write',
  'workspace:admin',
] as const;

const INLINE_CODE =
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-[13px] [&_code]:leading-none';

type ClientGuide = {
  name: string;
  logo: AppLogoName;
  instructions: React.ReactNode;
  learnMoreUrl: string;
};

const CLIENT_GUIDES: readonly ClientGuide[] = [
  {
    name: 'Claude',
    logo: 'claude',
    instructions: (
      <>
        Settings &gt; Connectors &gt; Add custom connector, paste the endpoint, add it, then
        authenticate from Search and tools.
      </>
    ),
    learnMoreUrl:
      'https://support.anthropic.com/en/articles/11175166-about-custom-integrations-using-remote-mcp',
  },
  {
    name: 'ChatGPT',
    logo: 'openai',
    instructions: (
      <>
        In the desktop app, open Settings &gt; MCP servers &gt; Add server, select Streamable HTTP,
        paste the endpoint, save and restart, then authenticate. ChatGPT web uses installed plugins
        rather than this direct URL flow.
      </>
    ),
    learnMoreUrl: 'https://learn.chatgpt.com/docs/extend/mcp',
  },
  {
    name: 'Codex',
    logo: 'codex',
    instructions: (
      <>
        Run <code>codex mcp add parcel --url https://mcp.parcelengineering.com/mcp</code>, then{' '}
        <code>codex mcp login parcel</code>. You can also use the shared desktop or IDE MCP settings
        surface.
      </>
    ),
    learnMoreUrl: 'https://learn.chatgpt.com/docs/extend/mcp',
  },
  {
    name: 'Cursor',
    logo: 'cursor',
    instructions: (
      <>
        Add a <code>parcel</code> URL entry to <code>~/.cursor/mcp.json</code> using the config
        below, then save, reload, and authenticate.
      </>
    ),
    learnMoreUrl: 'https://prod.cursor.com/help/customization/mcp',
  },
];

function formatGrantDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function cardStatus(status: DeveloperMcpGrant['status']) {
  if (status === 'active') return { variant: 'green' as const, label: 'Active' };
  if (status === 'expired') return { variant: 'gray' as const, label: 'Expired' };
  return { variant: 'gray' as const, label: 'Revoked' };
}

function RevokeGrantButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Button variant="ghost" size="icon" aria-label={label} onClick={onClick}>
      <Trash2 className="size-4" />
    </Button>
  );
}

function ClientGuides() {
  return (
    <section className="space-y-3" aria-labelledby="mcp-setup-heading">
      <div>
        <h3 id="mcp-setup-heading" className="text-sm font-medium">
          Connect a client
        </h3>
        <p className={`mt-1 text-sm leading-5 text-muted-foreground ${INLINE_CODE}`}>
          Use the Parcel MCP server URL: <code>{PARCEL_MCP_SERVER_URL}</code>
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {CLIENT_GUIDES.map((client) => (
          <IntegrationCard
            key={client.name}
            title={client.name}
            logo={
              <IntegrationTile>
                <AppLogo name={client.logo} size={20} className="text-foreground" aria-hidden />
              </IntegrationTile>
            }
            trailing={
              <a
                href={client.learnMoreUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <span className="sr-only">Learn more about {client.name}</span>
                <span aria-hidden>Learn more</span> <ExternalLink className="size-3" aria-hidden />
              </a>
            }
          >
            <div className={INLINE_CODE}>{client.instructions}</div>
          </IntegrationCard>
        ))}
      </div>
    </section>
  );
}

/** The empty and failed states a client list shares. */
function GrantPlaceholder({
  title,
  body,
  tone = 'empty',
}: { title?: string; body: string; tone?: 'empty' | 'error' }) {
  if (tone !== 'empty') {
    return (
      <div className="rounded-lg border border-dashed px-6 py-12 text-center">
        <p
          {...(tone === 'error' ? { role: 'alert' as const } : {})}
          className={cn('text-sm', tone === 'error' ? 'text-destructive' : 'text-muted-foreground')}
        >
          {body}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
      <PlugZap className="size-8 text-muted-foreground" aria-hidden />
      {title ? <p className="text-sm font-medium">{title}</p> : null}
      <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

/** A titled client list with an optional header action. Both lists read the same. */
function GrantSection({
  id,
  title,
  action,
  children,
}: { id: string; title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="space-y-3" aria-labelledby={id}>
      <div className="flex items-center justify-between gap-3">
        <h3 id={id} className="text-sm font-medium text-muted-foreground">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function GrantList({
  grants,
  onRevoke,
}: { grants: DeveloperMcpGrant[]; onRevoke: (grant: DeveloperMcpGrant) => void }) {
  if (grants.length === 0) {
    return (
      <GrantPlaceholder
        title="No MCP clients connected"
        body="Connect Claude, ChatGPT, Codex, Cursor, or another Streamable HTTP client to get started."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[220px]">Client</TableHead>
            <TableHead>Scopes</TableHead>
            <TableHead className="w-[110px]">Status</TableHead>
            <TableHead className="w-[120px]">Last used</TableHead>
            <TableHead className="w-[48px]">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {grants.map((grant) => {
            const status = cardStatus(grant.status);
            return (
              <TableRow key={grant.id}>
                <TableCell className="max-w-[220px] truncate font-medium" title={grant.clientLabel}>
                  {grant.clientLabel}
                </TableCell>
                <TableCell className="text-muted-foreground">{grant.scopes.join(', ')}</TableCell>
                <TableCell>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatGrantDate(grant.lastUsedAt)}
                </TableCell>
                <TableCell>
                  {grant.status !== 'revoked' ? (
                    <RevokeGrantButton
                      label={`Revoke ${grant.clientLabel}`}
                      onClick={() => onRevoke(grant)}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function WorkspaceGrantTable({
  grants,
  onRevoke,
}: {
  grants: DeveloperMcpWorkspaceGrant[];
  onRevoke: (grant: DeveloperMcpWorkspaceGrant) => void;
}) {
  if (grants.length === 0) {
    return (
      <GrantSection id="workspace-clients-heading" title="Workspace clients">
        <GrantPlaceholder
          title="No workspace clients connected"
          body="When a teammate connects an MCP client to this workspace, it appears here."
        />
      </GrantSection>
    );
  }

  return (
    <GrantSection id="workspace-clients-heading" title="Workspace clients">
      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Client</TableHead>
              <TableHead className="w-[110px]">Status</TableHead>
              <TableHead className="w-[120px]">Last used</TableHead>
              <TableHead className="w-[48px]">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grants.map((grant) => {
              const status = cardStatus(grant.status);
              return (
                <TableRow key={grant.id}>
                  <TableCell>{grant.member.name}</TableCell>
                  <TableCell className="font-medium">{grant.clientLabel}</TableCell>
                  <TableCell>
                    <Badge variant={status.variant}>{status.label}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatGrantDate(grant.lastUsedAt)}
                  </TableCell>
                  <TableCell>
                    {grant.status !== 'revoked' ? (
                      <RevokeGrantButton
                        label={`Revoke ${grant.clientLabel} for ${grant.member.name}`}
                        onClick={() => onRevoke(grant)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </GrantSection>
  );
}

type RevokeTarget = {
  grant: DeveloperMcpGrant;
  memberName: string | null;
};

export function DeveloperMcpSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const queryClient = useQueryClient();
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug),
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id ?? null;
  const grantsQuery = useQuery({
    queryKey: ['developer-mcp-grants', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: () => listDeveloperMcpGrants(workspaceId ?? ''),
  });
  const [revokeTarget, setRevokeTarget] = React.useState<RevokeTarget | null>(null);
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [revokeError, setRevokeError] = React.useState<string | null>(null);
  const [bulkError, setBulkError] = React.useState<string | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['developer-mcp-grants', workspaceId] });

  const revokeOne = async () => {
    if (!workspaceId || !revokeTarget) return;
    setSaving(true);
    setRevokeError(null);
    try {
      await revokeDeveloperMcpGrant(workspaceId, revokeTarget.grant.id);
      setRevokeTarget(null);
      await refresh();
    } catch {
      setRevokeError('Unable to revoke this MCP client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const revokeAll = async () => {
    if (!workspaceId) return;
    setSaving(true);
    setBulkError(null);
    try {
      await revokeAllMyDeveloperMcpGrants(workspaceId);
      setBulkOpen(false);
      await refresh();
    } catch {
      setBulkError('Unable to revoke your MCP clients. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const grants = grantsQuery.data;
  const loadingGrants =
    Boolean(workspaceSlug) &&
    (workspaceQuery.isPending || (workspaceQuery.isSuccess && grantsQuery.isPending));
  const grantLoadError = workspaceQuery.isError
    ? 'Unable to load this workspace.'
    : grantsQuery.isError
      ? 'Unable to load MCP clients.'
      : null;
  const openRevoke = (grant: DeveloperMcpGrant, memberName: string | null = null) => {
    setRevokeError(null);
    setRevokeTarget({ grant, memberName });
  };
  const closeRevoke = () => {
    setRevokeError(null);
    setRevokeTarget(null);
  };
  const openBulk = () => {
    setBulkError(null);
    setBulkOpen(true);
  };
  const closeBulk = () => {
    setBulkError(null);
    setBulkOpen(false);
  };
  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Developer"
        title="MCP"
        description="Connect AI clients to Parcel with the Model Context Protocol."
      />
      <McpClientHero />
      <div className="space-y-8">
        {grants && grants.plan !== 'pro' ? (
          <div className="rounded-lg border border-dashed px-6 py-5 text-sm text-muted-foreground">
            Developer MCP is available on Pro. You can still review and revoke clients that were
            connected before this workspace changed plans.
          </div>
        ) : null}
        <ClientGuides />
        <McpManualConfig configJson={PARCEL_MCP_CLIENT_CONFIG_JSON} />
        <section className="space-y-3" aria-labelledby="mcp-scopes-heading">
          <div>
            <h3 id="mcp-scopes-heading" className="text-sm font-medium">
              Supported scopes
            </h3>
            <p className="text-sm text-muted-foreground">
              Approve only the workspace access a client needs.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {SUPPORTED_SCOPES.map((scope) => (
              <Badge key={scope} variant="secondary">
                {scope}
              </Badge>
            ))}
          </div>
        </section>
        <GrantSection
          id="my-clients-heading"
          title="My MCP clients"
          action={
            <Button
              variant="outline"
              onClick={openBulk}
              disabled={!grants || !grants.mine.some((grant) => grant.status !== 'revoked')}
            >
              Revoke all
            </Button>
          }
        >
          {loadingGrants ? (
            <ListSkeleton rows={2} className="rounded-lg border" />
          ) : grantLoadError ? (
            <GrantPlaceholder tone="error" body={grantLoadError} />
          ) : grantsQuery.isSuccess ? (
            <GrantList grants={grantsQuery.data.mine} onRevoke={openRevoke} />
          ) : null}
        </GrantSection>
        {grants?.canManageWorkspaceGrants && grants.workspace ? (
          <WorkspaceGrantTable
            grants={grants.workspace}
            onRevoke={(grant) => openRevoke(grant, grant.member.name)}
          />
        ) : null}
      </div>
      <Dialog open={Boolean(revokeTarget)} onOpenChange={(open) => !open && closeRevoke()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke MCP client</DialogTitle>
            <DialogDescription>
              Revoke {revokeTarget?.grant.clientLabel}
              {revokeTarget?.memberName ? ` for ${revokeTarget.memberName}` : ''}? It will no longer
              be able to use this workspace.
            </DialogDescription>
          </DialogHeader>
          {revokeError ? (
            <p role="alert" className="text-sm text-destructive">
              {revokeError}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeRevoke}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={saving} onClick={revokeOne}>
              {saving ? 'Revoking…' : 'Revoke client'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={(open) => (open ? openBulk() : closeBulk())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke all your MCP clients</DialogTitle>
            <DialogDescription>
              Every client connected with your member account will lose access to this workspace.
            </DialogDescription>
          </DialogHeader>
          {bulkError ? (
            <p role="alert" className="text-sm text-destructive">
              {bulkError}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeBulk}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={saving} onClick={revokeAll}>
              {saving ? 'Revoking…' : 'Revoke all clients'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </SettingsShell>
  );
}
